import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CRMTenantBar from './CRMTenantBar';

const API = '/api';
const PRIMARY = '#6366f1';
const SUCCESS = '#22c55e';
const DANGER = '#ef4444';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';
const CARD = '#ffffff';
const TEXT = '#1e293b';
const MUTED = '#64748b';

function hdr() { return { Authorization: `Bearer ${localStorage.getItem('token')}` }; }

interface Tenant { id: number; company_name?: string; }

const CE_FIELDS = [
  'caller_number', 'caller_name', 'queue_name', 'agent_name', 'call_duration',
  'call_direction', 'call_start_time', 'call_end_time', 'disposition',
  'recording_url', 'transcript', 'summary', 'sentiment', 'tenant_name'
];

interface FieldMapping {
  id?: number;
  ce_field: string;
  crm_field: string;
  field_type: string;
  is_required: boolean;
}

export default function CRMFieldMappingPage({ user, tenants = [] }: { user: any; tenants?: Tenant[] }) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(
    isSuperAdmin ? (tenants?.[0]?.id ?? null) : (user?.tenant_id ?? null)
  );
  const tenantId = isSuperAdmin ? selectedTenantId : (user?.tenant_id ?? null);
  const qs = tenantId !== null ? `?tenant_id=${tenantId}` : '';

  const [connections, setConnections] = useState<any[]>([]);
  const [connId, setConnId] = useState<number | null>(null);
  const [objectType, setObjectType] = useState('Contact');
  const [crmFields, setCrmFields] = useState<string[]>([]);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [crmLoading, setCrmLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: 'ok' | 'err' } | null>(null);

  const loadConns = useCallback(async () => {
    if (tenantId === null) { setConnections([]); setConnId(null); return; }
    try {
      const r = await axios.get(`${API}/crm-integration/connections${qs}`, { headers: hdr() });
      const conns = (r.data.connections || []).filter((c: any) => c.status === 'connected');
      setConnections(conns);
      if (conns.length > 0 && !connId) setConnId(conns[0].id);
    } catch { }
  }, [tenantId, qs]);

  useEffect(() => { loadConns(); setMappings([]); setCrmFields([]); setMsg(null); }, [loadConns]);

  useEffect(() => {
    if (!connId || tenantId === null) { setCrmFields([]); setMappings([]); return; }
    const load = async () => {
      setCrmLoading(true);
      try {
        const [fRes, mRes] = await Promise.all([
          axios.get(`${API}/crm-integration/crm-fields/${connId}/${objectType}${qs}`, { headers: hdr() }),
          axios.get(`${API}/crm-integration/field-mapping${qs}&connection_id=${connId}&object_type=${objectType}`, { headers: hdr() }),
        ]);
        setCrmFields(fRes.data.fields?.map((f: any) => f.name || f) || []);
        setMappings(mRes.data.mappings || []);
      } catch (e: any) {
        setMsg({ text: e?.response?.data?.error || 'Failed to load fields', type: 'err' });
      } finally { setCrmLoading(false); }
    };
    load();
  }, [connId, objectType, tenantId]);

  const addRow = () => setMappings(prev => [...prev, { ce_field: CE_FIELDS[0], crm_field: '', field_type: 'text', is_required: false }]);
  const removeRow = (idx: number) => setMappings(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (idx: number, key: keyof FieldMapping, val: any) => setMappings(prev => prev.map((m, i) => i === idx ? { ...m, [key]: val } : m));

  const save = async () => {
    if (!connId || tenantId === null) { setMsg({ text: 'Select a connection first', type: 'err' }); return; }
    setSaving(true);
    setMsg(null);
    try {
      await axios.post(`${API}/crm-integration/field-mapping`, {
        tenant_id: tenantId,
        connection_id: connId,
        object_type: objectType,
        mappings,
      }, { headers: hdr() });
      setMsg({ text: 'Mappings saved', type: 'ok' });
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Save failed', type: 'err' });
    } finally { setSaving(false); }
  };

  const s: Record<string, React.CSSProperties> = {
    page: { padding: 24, background: BG, minHeight: '100vh' },
    title: { fontSize: 22, fontWeight: 700, color: TEXT, margin: '0 0 4px' },
    sub: { color: MUTED, fontSize: 14, marginBottom: 20 },
    toolbar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as const, alignItems: 'center' },
    select: { padding: '9px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, color: TEXT, background: CARD, minWidth: 200 },
    label: { fontSize: 12, fontWeight: 600, color: MUTED },
    card: { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
    th: { padding: '11px 14px', textAlign: 'left' as const, background: BG, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER}` },
    td: { padding: '10px 14px', borderBottom: `1px solid ${BG}` },
    input: { width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, color: TEXT, boxSizing: 'border-box' as const },
    sm_select: { width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, color: TEXT },
    footer: { padding: '14px 18px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 10, alignItems: 'center' },
    btn: { padding: '8px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    alert: { padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 },
  };

  const tenantName = isSuperAdmin && tenantId ? tenants.find(t => t.id === tenantId)?.company_name : null;

  return (
    <div style={s.page}>
      <h2 style={s.title}>CRM Field Mapping</h2>
      <p style={s.sub}>Map CallEssential call fields to CRM object fields{tenantName ? ` for ${tenantName}` : ''}.</p>

      <CRMTenantBar isSuperAdmin={isSuperAdmin} tenants={tenants} selectedTenantId={selectedTenantId} onSelect={(id) => { setSelectedTenantId(id); setConnId(null); setMappings([]); setCrmFields([]); setMsg(null); }} />

      {tenantId === null ? (
        <div style={{ textAlign: 'center', padding: 60, color: MUTED }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Select a tenant to configure their CRM field mappings</div>
        </div>
      ) : (
        <>
          {msg && (
            <div style={{ ...s.alert, background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2', color: msg.type === 'ok' ? '#166534' : '#991b1b', border: `1px solid ${msg.type === 'ok' ? '#86efac' : '#fca5a5'}` }}>
              {msg.text}
            </div>
          )}

          <div style={s.toolbar}>
            <div>
              <div style={s.label}>CRM Connection</div>
              <select style={s.select} value={connId ?? ''} onChange={e => setConnId(e.target.value ? parseInt(e.target.value) : null)}>
                <option value="">— Select connection —</option>
                {connections.map(c => <option key={c.id} value={c.id}>{c.provider_key} · {c.connection_name || c.id}</option>)}
              </select>
            </div>
            <div>
              <div style={s.label}>CRM Object Type</div>
              <select style={s.select} value={objectType} onChange={e => setObjectType(e.target.value)}>
                {['Contact', 'Lead', 'Account', 'Opportunity', 'Case'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {connections.length === 0 && (
            <div style={{ color: MUTED, padding: 20, background: CARD, borderRadius: 10, border: `1px solid ${BORDER}` }}>
              No connected CRMs for this tenant. Go to <strong>CRM Integrations</strong> to connect one first.
            </div>
          )}

          {connId && (
            crmLoading ? (
              <div style={{ color: MUTED, padding: 20 }}>Loading CRM fields...</div>
            ) : (
              <div style={s.card}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>CE Field</th>
                      <th style={s.th}>CRM Field</th>
                      <th style={s.th}>Type</th>
                      <th style={s.th}>Required</th>
                      <th style={s.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((m, i) => (
                      <tr key={i}>
                        <td style={s.td}>
                          <select style={s.sm_select} value={m.ce_field} onChange={e => updateRow(i, 'ce_field', e.target.value)}>
                            {CE_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </td>
                        <td style={s.td}>
                          {crmFields.length > 0 ? (
                            <select style={s.sm_select} value={m.crm_field} onChange={e => updateRow(i, 'crm_field', e.target.value)}>
                              <option value="">— select —</option>
                              {crmFields.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                          ) : (
                            <input style={s.input} value={m.crm_field} onChange={e => updateRow(i, 'crm_field', e.target.value)} placeholder="field_api_name" />
                          )}
                        </td>
                        <td style={s.td}>
                          <select style={s.sm_select} value={m.field_type} onChange={e => updateRow(i, 'field_type', e.target.value)}>
                            {['text', 'number', 'date', 'datetime', 'boolean', 'url'].map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td style={{ ...s.td, textAlign: 'center' as const }}>
                          <input type="checkbox" checked={m.is_required} onChange={e => updateRow(i, 'is_required', e.target.checked)} />
                        </td>
                        <td style={s.td}>
                          <button onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: DANGER, fontSize: 16 }}>×</button>
                        </td>
                      </tr>
                    ))}
                    {mappings.length === 0 && (
                      <tr><td colSpan={5} style={{ ...s.td, color: MUTED, textAlign: 'center', padding: 24 }}>No mappings yet. Click "Add Mapping" below.</td></tr>
                    )}
                  </tbody>
                </table>
                <div style={s.footer}>
                  <button style={{ ...s.btn, background: BG, color: TEXT, border: `1px solid ${BORDER}` }} onClick={addRow}>+ Add Mapping</button>
                  <button style={{ ...s.btn, background: PRIMARY, color: '#fff' }} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Mappings'}</button>
                  {msg && <span style={{ fontSize: 13, color: msg.type === 'ok' ? SUCCESS : DANGER, fontWeight: 600 }}>{msg.text}</span>}
                </div>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
